import styled, { css, keyframes } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';

import { LinkButton } from '../LinkButton';

interface HighlightedCustomDay {
  bg: string;
  color: string;
  hover: string;
}

const highlightedCustomDay = ({ bg, color, hover }: HighlightedCustomDay) => {
  const pulsateAnimation = keyframes`
    0% {
      background-color: ${bg};
    }
    50% {
      background-color: ${hover};
    }
    100% {
      background-color: ${bg};
    }
  `;

  return css`
    background-color: ${bg};
    color: ${color};

    &:hover {
      background-color: ${hover};
    }

    &.react-datepicker__day--selected {
      //animation: ${pulsateAnimation} 2s infinite;
      background-color: var(--c-semantic-info-500);
    }

    &.react-datepicker__day--disabled {
      background-color: gray;
      cursor: not-allowed;
    }

    &.react-datepicker__day--outside-month {
      color: var(--c-base-fg) !important;
      opacity: 0.75
    }
  `;
};

interface WrapperProps {
  $error?: boolean;
  $disabled?: boolean;
  $open?: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  position: relative;

  ${LinkButton} {
    user-select: none;
  }

  ${({ $error }) =>
    $error &&
    css`
      --border-color-override: var(--c-input-error-border);
      --label-color-override: var(--c-input-error-text);
    `}

  ${({ $open }) =>
    $open &&
    css`
      z-index: ${(props) => props.theme.zindex.inputDatePopper};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.37;
      cursor: not-allowed;
    `}

  // Overwrite styling when placed inline
  // This is a custom class that should be added to the wrapper when used inline
  &.react-datepicker-inline {
    .react-datepicker {
      box-shadow: none;
    }

    // Stretch columns (th, td)
    .react-datepicker__week,
    .react-datepicker__day-names {
      display: flex;
      justify-content: space-between;
    }

    // Fill columns
    .react-datepicker__day,
    .react-datepicker__day-name {
      flex: 1 1 auto;
      width: 100%;
    }
  }

  // Wrapper
  .react-datepicker-wrapper {
    display: block;
  }

  // Popper
  .react-datepicker-popper {
    &[data-placement^='bottom'] {
      padding-top: ${size(0.5)};
    }
  }

  // Root
  .react-datepicker {
    display: flex;
    flex-direction: column;
    background-color: var(--c-bg-main);
    border: 1px solid var(--c-border-main);
    border-radius: ${size(1)};
    box-shadow: 0 ${size(1)} ${size(4)} rgba(var(--c-overlay-rgb), 0.1);
    color: var(--c-text-main);
    font-family: var(--font-family);
    font-weight: var(--font-weight-regular);
    font-size: ${(props) => props.theme.font.size.small};
  }

  // Header
  .react-datepicker__header {
    padding-top: ${size(3)};
    padding-bottom: ${size(1)};
    padding-left: ${size(2)};
    padding-right: ${size(2)};
    background-color: transparent;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    border-bottom: 1px solid var(--c-border-main);
  }

  // Header: Navigation Arrows
  .react-datepicker__navigation {
    top: 28px; // Magic number: Header padding top + align with current month

    top: ${size(2)};
    width: ${size(5)};
    height: ${size(5)};
    border: none;
    border-radius: 50%;
    background-repeat: no-repeat;
    background-size: ${(props) => props.theme.font.size.xxsmall};
    background-position: 50%;

    &:hover {
      background-color: var(--c-accent-bg);
    }

    &--previous {
      left: ${size(2)};
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M11.4 0l1.4 1.3L5.9 8l6.9 6.7-1.4 1.3-6.9-6.7L3.2 8l1.4-1.3L11.4 0z" fill="%23282828"/></svg>');
    }

    &--next {
      right: ${size(2)};
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M4.6 16l-1.4-1.3L10.1 8 3.2 1.3 4.6 0l6.9 6.7L12.8 8l-1.4 1.3L4.6 16z" fill="%23282828"/></svg>');
    }
  }

  // Header: Navigation Arrow icons
  .react-datepicker__navigation-icon {
    display: none;
  }

  // Header: Current Month
  .react-datepicker__current-month {
    font-family: var(--font-family);
    font-weight: var(--font-weight-bold);
    text-transform: capitalize;
    font-size: 1em;
    margin-bottom: ${size(1)};
    user-select: none;
  }

  // Header: Selects
  .react-datepicker__header__dropdown {
    display: flex;
    align-items: stretch;
    margin-top: ${size(2)};
    margin-bottom: ${size(1)};
    margin-left: ${size(-0.5)};
    margin-right: ${size(-0.5)};
  }

  .react-datepicker__month-dropdown-container--select,
  .react-datepicker__year-dropdown-container--select {
    flex: 1 1 100%;
    padding: 0 ${size(0.5)};
    margin: 0;
  }

  .react-datepicker__month-select,
  .react-datepicker__year-select {
    display: block;
    appearance: none;
    width: 100%;
    height: ${size(5.5)};
    margin: 0;
    padding: 0;
    padding-left: ${size(1.5)};
    padding-right: calc(${(props) => props.theme.font.size.xxsmall} + ${size(2)});
    border: none;
    border-bottom: 1px solid var(--c-border-main);
    background-color: var(--c-accent-bg);
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 15 7 9h10l-5 6Z" fill="%23282828"/></svg>');
    background-repeat: no-repeat;
    background-size: ${size(2)};
    background-position: calc(100% - ${size(1.5)}) 50%;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
    font-size: 16px;
    font-family: var(--font-family);
    font-weight: var(--font-weight-regular);
    text-transform: capitalize;
    cursor: pointer;

    &[disabled] {
      opacity: 0.37;
      cursor: not-allowed;
    }

    &::-ms-expand {
      display: none;
    }

    option {
      color: var(--c-text-main);
    }
  }

  // Header: Days
  .react-datepicker__day-name {
    color: var(--c-text-muted);
    text-transform: capitalize;
  }

  // Months
  .react-datepicker__month {
    margin: 0;
    padding: ${size(1)} ${size(2)};
  }

  // Day, Day Name
  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: ${size(4.5)};
    line-height: ${size(4.5)};
    margin: ${size(0.25)};
  }

  // Day
  .react-datepicker__day {
    border-radius: var(--border-radius);

    &:hover {
      background-color: var(--c-accent-bg);
      border-radius: var(--border-radius);
    }

    // Overlay (tooltip) that can be shown on hover over a day
    .overlay {
      bottom: calc(100% + ${size(0.25)});
      padding: ${size(0.5)} ${size(1)};
      font-size: ${(props) => props.theme.font.size.xsmall};
      line-height: ${(props) => props.theme.font.lineHeight.main};
      pointer-events: none;
    }
  }

  // Day: Today
  .react-datepicker__day--today {
    font-family: var(--font-family);
    font-weight: var(--font-weight-bold);
  }

  // Day: Outside month
  .react-datepicker__day--outside-month {
    &:not(.react-datepicker__day--in-range) {
      &:not(.react-datepicker__day--today) {
        color: var(--c-text-muted);

        // Overwrite color if day is selected
        &.react-datepicker__day--selected {
          color: var(--c-primary-fg);
        }
      }
    }
  }

  // Day: Selected and end of selected range
  .react-datepicker__day--selected,
  .react-datepicker__day--in-range.react-datepicker__day--selected,
  .react-datepicker__day--in-range.react-datepicker__day--range-end {
    background-color: var(--c-primary-bg);
    color: var(--c-primary-fg);

    &:hover {
      background-color: var(--c-primary-bg);
    }
  }

  // Day: In selecting range
  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--selected) {
    background-color: var(--c-secondary-bg);
    color: var(--c-secondary-fg);

    &:hover {
      background-color: var(--c-secondary-bg);
    }
  }

  // Day: Highlighted
  .react-datepicker__day--highlighted {
    background-color: var(--c-semantic-success-500);
    color: var(--c-semantic-success-100);

    &:hover {
      background-color: var(--c-semantic-success-700);
    }
  }

  // Day: Holidays (used as blocked dates)
  .react-datepicker__day--holidays {
    // Styling when selected
    background-color: var(--c-semantic-warning-500);
    color: var(--c-semantic-warning-100);

    &:hover {
      background-color: var(--c-semantic-warning-500);
    }

    // Styling when not selected
    &:not(.react-datepicker__day--selected) {
      background-color: var(--c-semantic-warning-100);
      color: var(--c-semantic-warning-700);

      &:hover {
        background-color: var(--c-semantic-warning-300);
      }
    }
  }

  // Chidren container (footer)
  .react-datepicker__children-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${size(1)};
    width: 100%;
    margin: 0;
    padding: ${size(2)};
    border-top: 1px solid var(--c-border-main);
  }

  .react-datepicker__day--highlighted-custom-contains-bookings {
    ${highlightedCustomDay({
      bg: 'var(--c-semantic-warning-500)',
      color: 'var(--c-semantic-warning-100)',
      hover: 'var(--c-semantic-warning-700)',
    })}
  }

  .react-datepicker__day--highlighted-custom-fully-booked {
    ${highlightedCustomDay({
      bg: 'var(--c-semantic-error-500)',
      color: 'var(--c-semantic-error-100)',
      hover: 'var(--c-semantic-error-700)',
    })}
  }

  .react-datepicker__day--highlighted-custom-no-bookings {
    ${highlightedCustomDay({
      bg: 'var(--c-semantic-success-500)',
      color: 'var(--c-semantic-success-100)',
      hover: 'var(--c-semantic-success-700)',
    })}
  }
`;

export const Init = styled(ButtonReset).attrs(() => ({
  type: 'button',
}))`
  --gutter-inline: ${size(1.5)};
  --gutter-block: ${size(1)};
  --icon-size: ${size(2)};

  display: block;
  width: 100%;
  height: ${(props) => props.theme.distances.inputHeight};
  padding-top: var(--gutter-block);
  padding-bottom: var(--gutter-block);
  padding-left: var(--gutter-inline);
  padding-right: calc(var(--icon-size) + (var(--gutter-inline) * 2));
  border: 0;
  border-bottom: 1px solid var(--c-border-main);
  background-color: var(--c-accent-bg);
  color: var(--c-accent-fg);
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 15 7 9h10l-5 6Z" fill="%23282828"/></svg>');
  background-repeat: no-repeat;
  background-size: var(--icon-size);
  background-position: calc(100% - ${size(2)}) 50%;
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  text-align: left;

  &[disabled] {
    cursor: not-allowed;
  }
`;

export const InitValue = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
`;

export const InitLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${size(0.5)};
  font-size: ${(props) => props.theme.font.size.xsmall};
  line-height: ${(props) => props.theme.font.lineHeight.alt};
  color: var(--label-color-override, var(--c-text-muted));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  pointer-events: none;
  line-height: 1.2;
  margin-bottom: ${size(0.25)};
`;

export const Footer = styled.div`
  padding: ${size(2)};
  border-top: 1px solid var(--c-border-main);
`;
